import React from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { Notification } from '../../types/notifications';
import { getNotificationType, NOTIFICATION_TYPES, formatNotificationDate } from '../../types/notifications';
import { useNavigate } from 'react-router-dom';
import { markAsRead, deleteNotification } from '../../hooks/useNotifications';
import { useNotification } from '../../contexts/NotificationContext';

interface NotificationItemProps {
  notification: Notification;
  onMarkAsRead: (id: number) => void;
  onDelete: (id: number) => void;
}

const NotificationItem: React.FC<NotificationItemProps> = ({
  notification,
  onMarkAsRead,
  onDelete,
}) => {
  const { colors } = useTheme();
  const navigate = useNavigate();
  const { showError } = useNotification();
  const type = getNotificationType(notification.entityType);
  const typeConfig = NOTIFICATION_TYPES[type];
  const isRead = !!notification.readAt;

  const handleClick = async () => {
    if (!isRead) {
      try {
        await markAsRead(notification.id);
        onMarkAsRead(notification.id);
      } catch (error: any) {
        showError(error?.message || 'Failed to mark notification as read');
      }
    }

    if (notification.link) {
      navigate(notification.link);
    }
  };

  const handleDelete = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await deleteNotification(notification.id);
      onDelete(notification.id);
    } catch (error: any) {
      showError(error?.message || 'Failed to delete notification');
    }
  };

  return (
    <div
      onClick={handleClick}
      className={`
        p-3 border-b ${colors.border} ${colors.interactiveHover} cursor-pointer transition-colors
        ${!isRead ? 'bg-blue-50 dark:bg-blue-900/20' : ''}
      `}
    >
      <div className="flex items-start gap-3">
        <div className={`flex-shrink-0 ${typeConfig.color}`}>
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <div className="flex-1 min-w-0">
          <p className={`text-sm ${!isRead ? 'font-semibold' : 'font-normal'} ${colors.text}`}>
            {notification.message}
          </p>
          <p className={`text-xs ${colors.textMuted} mt-1`}>
            {formatNotificationDate(notification.createdAt)}
          </p>
        </div>
        {!isRead && (
          <div className="flex-shrink-0">
            <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
          </div>
        )}
        <button
          onClick={handleDelete}
          className={`flex-shrink-0 ${colors.textMuted} hover:text-red-600 transition-colors`}
          title="Delete notification"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default NotificationItem;
